import React, { useState, useEffect } from 'react';
import { apiService, SuperAdminOffering, HotDealItem } from '../services/api';
import './SuperAdminViews.css';

const AllHotDealsView: React.FC = () => {
  // All offerings available to add to the carousel
  const [offerings, setOfferings] = useState<SuperAdminOffering[]>([]);
  // Ordered list of offering ids currently in the hot-deals carousel
  const [orderedIds, setOrderedIds] = useState<number[]>([]);
  // Offering id currently selected in the "add" picker
  const [addSelection, setAddSelection] = useState<string>('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [hotDeals, allOfferings] = await Promise.all([
        apiService.getHotDeals(),
        apiService.getSuperAdminOfferings(),
      ]);
      setOfferings(allOfferings);
      // Server returns hot deals already ordered by hot_sort_order
      setOrderedIds(hotDeals.map(d => d.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hot deals');
    } finally {
      setLoading(false);
    }
  };

  const getOffering = (id: number): SuperAdminOffering | undefined =>
    offerings.find(o => o.id === id);

  // Offerings not yet in the carousel, for the add picker
  const availableOfferings = offerings.filter(o => !orderedIds.includes(o.id));

  const handleAdd = () => {
    const id = parseInt(addSelection, 10);
    if (!Number.isNaN(id) && !orderedIds.includes(id)) {
      setOrderedIds(prev => [...prev, id]);
      setAddSelection('');
      setSaveSuccess(false);
    }
  };

  const handleRemove = (id: number) => {
    setOrderedIds(prev => prev.filter(o => o !== id));
    setSaveSuccess(false);
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= orderedIds.length) return;
    setOrderedIds(prev => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const items: HotDealItem[] = orderedIds.map((id, index) => ({
      offering_id: id,
      hot_sort_order: index,
    }));

    try {
      const result = await apiService.setHotDeals(items);
      // Sync from the authoritative server response
      setOrderedIds(result.map(d => d.id));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save hot deals');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-view">
        <div className="loading-state">
          <p>Loading hot deals...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-view">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchData} className="add-button">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-view">
      <div className="view-header">
        <div>
          <h2>Hot Deals Carousel</h2>
          <p className="view-description">
            Curate the featured deals shown to end users. Ordering here controls the carousel order.
          </p>
        </div>
        <button onClick={handleSave} className="add-button" disabled={saving}>
          {saving ? 'Saving...' : '💾 Save Carousel'}
        </button>
      </div>

      {saveError && <div className="error-message">⚠ {saveError}</div>}
      {saveSuccess && <div className="success-message">Hot deals updated successfully!</div>}

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-value">{orderedIds.length}</div>
          <div className="stat-label">In Carousel</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{availableOfferings.length}</div>
          <div className="stat-label">Available to Add</div>
        </div>
      </div>

      {/* Add an offering to the carousel */}
      <div className="view-controls">
        <div className="filter-box" style={{ flex: 1 }}>
          <select
            value={addSelection}
            onChange={(e) => setAddSelection(e.target.value)}
            disabled={availableOfferings.length === 0}
          >
            <option value="">
              {availableOfferings.length === 0
                ? 'All offerings are already in the carousel'
                : 'Select an offering to add...'}
            </option>
            {availableOfferings.map(o => (
              <option key={o.id} value={o.id}>
                {o.name}
                {o.business_name ? ` — ${o.business_name}` : ''}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleAdd}
          className="add-button"
          disabled={!addSelection}
        >
          ➕ Add to Carousel
        </button>
      </div>

      {orderedIds.length === 0 ? (
        <div className="empty-state">
          <p>No hot deals selected. Add offerings above and save to build the carousel.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Offering</th>
                <th>Business</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orderedIds.map((id, index) => {
                const offering = getOffering(id);
                return (
                  <tr key={id}>
                    <td>
                      <strong>{index + 1}</strong>
                    </td>
                    <td>
                      <div className="name-cell">
                        {offering?.image ? (
                          <img
                            src={offering.image}
                            alt={offering.name}
                            style={{ width: 40, height: 40, borderRadius: '8px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="avatar" style={{ backgroundColor: '#0d1b2a' }}>🎁</div>
                        )}
                        <div>
                          <strong>{offering?.name || `Offering #${id}`}</strong>
                        </div>
                      </div>
                    </td>
                    <td>
                      {offering?.business_name ||
                        (offering?.business_id ? `Business #${offering.business_id}` : '—')}
                    </td>
                    <td>
                      <div className="action-buttons-group">
                        <button
                          className="action-button"
                          onClick={() => moveItem(index, -1)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          className="action-button"
                          onClick={() => moveItem(index, 1)}
                          disabled={index === orderedIds.length - 1}
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          className="action-button deactivate"
                          onClick={() => handleRemove(id)}
                          title="Remove from carousel"
                        >
                          🗑 Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AllHotDealsView;
